// Themed SweetAlert2 wrapper. All app-wide messaging goes through here so
// the look stays consistent (Inter font, brand blue + teal, soft shadows,
// our own button classes). Import the named helpers; do not call Swal
// directly from pages.

import Swal from 'sweetalert2';

const baseClasses = {
  popup: 'swal-app',
  title: 'swal-app-title',
  htmlContainer: 'swal-app-text',
  actions: 'swal-app-actions',
  confirmButton: 'btn-primary swal-app-btn',
  cancelButton: 'btn-ghost swal-app-btn',
  denyButton: 'btn-danger swal-app-btn',
  icon: 'swal-app-icon',
};

const base = (opts = {}) =>
  Swal.fire({
    buttonsStyling: false,
    reverseButtons: true,
    focusConfirm: false,
    allowOutsideClick: () => !Swal.isLoading(),
    showClass: { popup: 'swal-app-show' },
    hideClass: { popup: 'swal-app-hide' },
    customClass: baseClasses,
    ...opts,
  });

const ToastBase = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3200,
  timerProgressBar: true,
  buttonsStyling: false,
  customClass: { popup: 'swal-app-toast' },
  didOpen: (el) => {
    el.addEventListener('mouseenter', Swal.stopTimer);
    el.addEventListener('mouseleave', Swal.resumeTimer);
  },
});

// ── Primary helpers ───────────────────────────────────────────────────────────
export const swalSuccess = (title, text, opts = {}) =>
  base({ icon: 'success', title, text, confirmButtonText: 'OK', ...opts });

export const swalError = (title, text, opts = {}) =>
  base({ icon: 'error', title, text, confirmButtonText: 'Close', ...opts });

export const swalWarning = (title, text, opts = {}) =>
  base({ icon: 'warning', title, text, confirmButtonText: 'Got it', ...opts });

export const swalInfo = (title, text, opts = {}) =>
  base({ icon: 'info', title, text, confirmButtonText: 'OK', ...opts });

// Returns a boolean (resolved value of isConfirmed)
export const swalConfirm = async (title, text, opts = {}) => {
  const res = await base({
    icon: 'question',
    title,
    text,
    showCancelButton: true,
    confirmButtonText: opts.confirmButtonText || 'Yes, continue',
    cancelButtonText: opts.cancelButtonText || 'Cancel',
    ...opts,
  });
  return res.isConfirmed;
};

// Red-button confirm for destructive actions (suspend, delete, etc.)
export const swalDangerConfirm = async (title, text, opts = {}) => {
  const res = await base({
    icon: 'warning',
    title,
    text,
    showCancelButton: true,
    confirmButtonText: opts.confirmButtonText || 'Yes, do it',
    cancelButtonText: opts.cancelButtonText || 'Cancel',
    customClass: { ...baseClasses, confirmButton: 'btn-danger swal-app-btn swal-app-danger' },
    ...opts,
  });
  return res.isConfirmed;
};

// Toasts — short non-blocking confirmation in the top-right
export const toastSuccess = (title) => ToastBase.fire({ icon: 'success', title });
export const toastError   = (title) => ToastBase.fire({ icon: 'error',   title });
export const toastWarning = (title) => ToastBase.fire({ icon: 'warning', title });
export const toastInfo    = (title) => ToastBase.fire({ icon: 'info',    title });

// Pull error message out of an axios error in one place.
export const showAxiosError = (err, fallback = 'Something went wrong') => {
  const msg = err?.response?.data?.message || err?.message || fallback;
  return swalError('Action failed', msg);
};

const swal = {
  success: swalSuccess,
  error: swalError,
  warning: swalWarning,
  info: swalInfo,
  confirm: swalConfirm,
  dangerConfirm: swalDangerConfirm,
  toast: {
    success: toastSuccess,
    error: toastError,
    warning: toastWarning,
    info: toastInfo,
  },
  fromAxios: showAxiosError,
};

export default swal;
